import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { CloseIcon } from '#/shared/ui/icons'
import { VoiceInputButton } from '#/shared/ui/voice-recording-island'

interface CommentTab {
  value: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  /** Show a notification dot on the tab icon (e.g. the shipment has this item).
   *  Only rendered in the `compact` variant (the sidebar detail), not `default`. */
  badge?: boolean
}

function TabIcon({
  Icon,
  badge,
  className,
}: {
  Icon: React.ComponentType<{ className?: string }>
  badge?: boolean
  className?: string
}) {
  return (
    <span className="relative shrink-0">
      <Icon className={className} />
      {badge && (
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -right-1 size-2 rounded-full bg-blue-500 ring-2 ring-white"
        />
      )}
    </span>
  )
}

type CommentToolVariant = 'default' | 'compact'

interface CommentToolProps {
  tabs: CommentTab[]
  value?: string
  onValueChange?: (value: string) => void
  activeTab?: string | null
  defaultActiveTab?: string | null
  onActiveTabChange?: (tab: string | null) => void
  placeholder?: string
  restingPlaceholder?: string
  activateTabOnPlaceholderClick?: string
  variant?: CommentToolVariant
  className?: string
  children?: React.ReactNode
}

interface IndicatorStyle {
  left: number
  width: number
  height: number
}

function TabButton({
  tab,
  active,
  onClick,
  innerRef,
}: {
  tab: CommentTab
  active: boolean
  onClick: () => void
  innerRef: React.RefCallback<HTMLButtonElement>
}) {
  const Icon = tab.icon
  return (
    <div className="flex h-10 shrink-0 items-center px-0.5 pt-0.5">
      <button
        ref={innerRef}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={onClick}
        className={cn(
          'relative flex h-full cursor-pointer items-center gap-1.75 overflow-hidden rounded-t pt-1.25 pr-1.75 pb-1.75 pl-1.25 text-sm transition-colors',
          active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 bg-warm-50 transition-[height] duration-300 ease-out',
            active ? 'h-full' : 'h-0',
          )}
        />
        <TabIcon Icon={Icon} className="relative size-5" />
        <span className="relative whitespace-nowrap">{tab.label}</span>
      </button>
    </div>
  )
}

function CompactTabButton({
  tab,
  active,
  onClick,
}: {
  tab: CommentTab
  active: boolean
  onClick: () => void
}) {
  const Icon = tab.icon
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={(event) => {
        event.currentTarget.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        })
        onClick()
      }}
      className={cn(
        'flex min-w-13 shrink-0 select-none flex-col items-center justify-center gap-1 px-2 py-1.25 text-xs transition-colors',
        active
          ? 'bg-gray-100 text-gray-900'
          : 'text-gray-500 hover:text-gray-900',
      )}
    >
      <TabIcon Icon={Icon} badge={tab.badge} className="size-4" />
      <span className="whitespace-nowrap">{tab.label}</span>
    </button>
  )
}

/** Cap the open dropdown at the space left below it so it fills down to the
 *  sidebar bottom — fully covering whatever sits beneath (e.g. the chat
 *  composer) — and the body scrolls instead of pushing the footer off screen.
 *  Re-measures on open and on resize. */
function usePanelMaxHeight(open: boolean) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [maxHeight, setMaxHeight] = React.useState<number | null>(null)
  React.useLayoutEffect(() => {
    if (!open) {
      setMaxHeight(null)
      return
    }
    const measure = () => {
      const el = ref.current
      if (!el) return
      const top = el.getBoundingClientRect().top
      setMaxHeight(Math.max(200, window.innerHeight - top))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [open])
  return { ref, maxHeight }
}

function CommentToolCompact({
  tabs,
  activeValue,
  setActive,
  className,
  children,
}: {
  tabs: CommentTab[]
  activeValue: string | null
  setActive: (next: string | null) => void
  className?: string
  children?: React.ReactNode
}) {
  const { t } = useTranslation()
  const { ref: panelRef, maxHeight } = usePanelMaxHeight(activeValue != null)
  return (
    <div className={cn('relative flex w-full min-w-0 flex-col', className)}>
      <div className="flex w-full min-w-0 items-center border-b border-gray-200 pr-4">
        <div
          role="tablist"
          className="flex min-w-0 flex-1 items-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((tab) => (
            <CompactTabButton
              key={tab.value}
              tab={tab}
              active={tab.value === activeValue}
              onClick={() => setActive(tab.value)}
            />
          ))}
        </div>
        {activeValue != null && (
          <button
            type="button"
            aria-label={t('commentTool.close')}
            onClick={() => setActive(null)}
            className="flex size-6 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <CloseIcon className="size-3" />
          </button>
        )}
      </div>
      {activeValue != null && (
        <div ref={panelRef} className="absolute inset-x-0 top-full z-30">
          <div
            key={activeValue}
            style={maxHeight != null ? { maxHeight } : undefined}
            className="flex w-full flex-col overflow-y-auto overscroll-contain border-b border-gray-200 bg-gray-100 shadow-toolbar animate-in fade-in slide-in-from-top-2 duration-300 ease-out"
          >
            {children}
          </div>
        </div>
      )}
    </div>
  )
}

interface CommentToolDefaultProps extends Omit<
  CommentToolProps,
  'activeTab' | 'defaultActiveTab' | 'variant'
> {
  activeValue: string | null
  setActive: (next: string | null) => void
}

/** The default editing surface: a plain auto-rows textarea with the floating
 *  voice-dictation mic at its bottom-right. Rendered only when no custom tab
 *  `children` are supplied. */
function CommentToolTextarea({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  placeholder?: string
}) {
  return (
    <div className="relative">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={1}
        className="w-full resize-none bg-transparent pt-2.5 pr-10 pb-7.5 pl-2.5 text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none"
      />
      <VoiceInputButton
        value={value}
        onChange={onChange}
        className="absolute right-2 bottom-2"
      />
    </div>
  )
}

function CommentToolDefault({
  tabs,
  value,
  onValueChange,
  activeValue,
  setActive,
  placeholder,
  restingPlaceholder,
  activateTabOnPlaceholderClick,
  className,
  children,
}: CommentToolDefaultProps) {
  const [internalValue, setInternalValue] = React.useState('')
  const currentValue = value ?? internalValue
  const setValue = (next: string) => {
    if (value === undefined) setInternalValue(next)
    onValueChange?.(next)
  }

  const tabRefs = React.useRef<Record<string, HTMLButtonElement | null>>({})
  const tablistRef = React.useRef<HTMLDivElement | null>(null)
  const [indicator, setIndicator] = React.useState<IndicatorStyle | null>(null)

  const updateIndicator = React.useCallback(() => {
    if (activeValue == null) {
      setIndicator(null)
      return
    }
    const el = tabRefs.current[activeValue]
    const container = tablistRef.current
    if (!el || !container) return
    const elRect = el.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    setIndicator({
      left: elRect.left - containerRect.left + container.scrollLeft,
      width: elRect.width,
      height: elRect.height,
    })
  }, [activeValue])

  React.useLayoutEffect(() => {
    updateIndicator()
  }, [updateIndicator, tabs.length])

  React.useEffect(() => {
    const handler = () => updateIndicator()
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [updateIndicator])

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col items-start rounded-md border border-gray-200 bg-white',
        className,
      )}
    >
      <div className="relative w-full min-w-0">
        <div
          ref={tablistRef}
          role="tablist"
          className="relative flex w-full min-w-0 items-start overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((tab) => (
            <TabButton
              key={tab.value}
              tab={tab}
              active={tab.value === activeValue}
              onClick={() => setActive(tab.value)}
              innerRef={(el) => {
                tabRefs.current[tab.value] = el
              }}
            />
          ))}
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gray-200"
        />
        {indicator && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 h-px bg-gray-900 transition-[left,width] duration-300 ease-out"
            style={{
              left: indicator.left,
              width: indicator.width,
            }}
          />
        )}
      </div>

      <div
        key={activeValue ?? '__resting__'}
        className="w-full animate-in fade-in slide-in-from-top-1 duration-200 ease-out"
      >
        {activeValue == null ? (
          <button
            type="button"
            onClick={() => {
              const fallback = tabs[0]?.value
              const next = activateTabOnPlaceholderClick ?? fallback
              if (next) setActive(next)
            }}
            className="w-full cursor-text px-2.5 pt-2.5 pb-7.5 text-left text-sm text-gray-300 hover:text-gray-400 focus:outline-none"
          >
            {restingPlaceholder ?? placeholder}
          </button>
        ) : (
          (children ?? (
            <CommentToolTextarea
              value={currentValue}
              onChange={setValue}
              placeholder={placeholder}
            />
          ))
        )}
      </div>
    </div>
  )
}

function CommentTool({
  activeTab,
  defaultActiveTab,
  onActiveTabChange,
  variant = 'default',
  ...rest
}: CommentToolProps) {
  const [internalActive, setInternalActive] = React.useState<string | null>(
    defaultActiveTab !== undefined ? defaultActiveTab : null,
  )
  const activeValue = activeTab !== undefined ? activeTab : internalActive
  const setActive = (next: string | null) => {
    if (activeTab === undefined) setInternalActive(next)
    onActiveTabChange?.(next)
  }

  if (variant === 'compact') {
    return (
      <CommentToolCompact
        tabs={rest.tabs}
        activeValue={activeValue}
        setActive={setActive}
        className={rest.className}
      >
        {rest.children}
      </CommentToolCompact>
    )
  }

  return (
    <CommentToolDefault
      {...rest}
      activeValue={activeValue}
      setActive={setActive}
    />
  )
}

export { CommentTool }
export type { CommentToolProps, CommentTab }
